import type { Role } from '@/types';
import { ALL_PERMISSION_SLUGS } from '@/constants/permissions';
import type { PermissionSlug } from '@/constants/permissions';

const knownPermissionSlugs = new Set<string>(ALL_PERMISSION_SLUGS);
const warnedUnknownPermissionSlugs = new Set<string>();

function isPermissionSlug(value: string): value is PermissionSlug {
  return knownPermissionSlugs.has(value);
}

function sanitizePermissions(permissions: string[] | undefined): PermissionSlug[] {
  if (!permissions || permissions.length === 0) {
    return [];
  }

  const validPermissions: PermissionSlug[] = [];

  for (const permission of permissions) {
    if (isPermissionSlug(permission)) {
      validPermissions.push(permission);
      continue;
    }

    if (!warnedUnknownPermissionSlugs.has(permission)) {
      warnedUnknownPermissionSlugs.add(permission);
      console.warn('[auth] Unknown permission slug received from API:', permission);
    }
  }

  return validPermissions;
}

export class UserEntity {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly email: string,
    public readonly username: string | null,
    public readonly roles: Role[],
    public readonly permissions: PermissionSlug[],
    public readonly isActive: boolean,
    public readonly emailVerifiedAt: string | null,
    public readonly createdAt: Date,
    public readonly locale: string | null = null,
  ) {}

  isEmailVerified(): boolean {
    return this.emailVerifiedAt !== null;
  }

  static fromApi(data: {
    id: string;
    name: string;
    email: string;
    username: string | null;
    roles: Role[];
    permissions?: string[];
    is_active: boolean;
    email_verified_at?: string | null;
    created_at: string;
    locale?: string | null;
  }): UserEntity {
    return new UserEntity(
      data.id,
      data.name,
      data.email,
      data.username,
      data.roles,
      sanitizePermissions(data.permissions),
      data.is_active,
      data.email_verified_at ?? null,
      new Date(data.created_at),
      data.locale ?? null,
    );
  }

  toPlain() {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      username: this.username,
      roles: this.roles,
      permissions: this.permissions,
      is_active: this.isActive,
      email_verified_at: this.emailVerifiedAt,
      created_at: this.createdAt.toISOString(),
      locale: this.locale,
    };
  }
}
